import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { flexRender } from '@tanstack/react-table'
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react'

// Collect metas from all ancestor (group) columns for the given column
const getColumnAncestorMetas = (column) => {
  const metas = []
  let parent = column?.parent
  while (parent) {
    const meta = parent.columnDef?.meta
    if (meta) metas.push(meta)
    parent = parent.parent
  }
  return metas
}

const DataTableWithBorder = ({ table, onCellClick, className, footer }) => {
  if (!table) {
    return null
  }
  return (
    <Table className={cn(className)}>
      <TableHeader className="sticky top-0 z-21 bg-muted">
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              const canSort =
                !header.isPlaceholder &&
                header.column.columnDef.enableSorting === true
              const sortDirection = canSort
                ? header.column.getIsSorted()
                : false

              const isSticky = header.column.columnDef.meta?.sticky
              const stickyLeft = header.column.columnDef.meta?.stickyLeft

              return (
                <TableHead
                  key={header.id}
                  colSpan={header.colSpan}
                  onClick={
                    canSort
                      ? header.column.getToggleSortingHandler()
                      : undefined
                  }
                  className={cn(
                    'border-b border-black text-bold bg-muted',
                    canSort && 'cursor-pointer select-none',
                    isSticky && `sticky z-22 bg-muted`,
                    stickyLeft,
                  )}
                >
                  <div className="flex items-center gap-1">
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                    {canSort && (
                      <div className="w-4">
                        {sortDirection === 'asc' ? (
                          <ArrowUp className="h-4 w-4" />
                        ) : sortDirection === 'desc' ? (
                          <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4" />
                        )}
                      </div>
                    )}
                  </div>
                </TableHead>
              )
            })}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows?.length ? (
          table.getRowModel().rows.map((row, i) => (
            <TableRow
              key={row.id}
              className={cn(
                'group data-[state=selected]:bg-yellow-100 hover:bg-yellow-100',
                i % 2 === 0 ? 'bg-gray-200' : 'bg-white',
              )}
              data-state={row.getIsSelected() && 'selected'}
              onClick={(e) => {
                // 如果点击的是可点击的 cell（有 data-clickable-cell 属性），不触发 toggleSelected
                const target = e.target
                const clickableCell = target.closest(
                  '[data-clickable-cell="true"]',
                )
                if (!clickableCell) {
                  row.toggleSelected()
                }
              }}
            >
              {row.getVisibleCells().map((cell) => {
                // Leaf column metas
                let isSticky = cell.column.columnDef.meta?.sticky
                let stickyLeft = cell.column.columnDef.meta?.stickyLeft
                let isLight = cell.column.meta?.isLight

                // Inherit/merge metas from ancestor (group) columns
                const ancestorMetas = getColumnAncestorMetas(cell.column)
                if (ancestorMetas.length) {
                  const ancestorIsLight = ancestorMetas.some((m) => m?.isLight)
                  const ancestorCellClassName = ancestorMetas
                    .map((m) => m?.cellClassName)
                    .filter(Boolean)
                    .join(' ')

                  isLight = isLight || ancestorIsLight

                  // Attach on the cell for later use in className composition
                  cell._groupCellClassName = ancestorCellClassName || undefined
                }
                isLight = isLight && i % 2 === 0 && !row.getIsSelected()
                // 检查 cell 的 meta 中是否有可点击信息
                const cellMeta = cell.column.columnDef.meta
                const isClickableCell = cellMeta?.isClickableCell
                const handleCellClick = (e) => {
                  if (isClickableCell && cellMeta?.onCellClickHandler) {
                    e.stopPropagation()
                    e.preventDefault()
                    cellMeta.onCellClickHandler(cellMeta.clickableData)
                  } else if (onCellClick) {
                    onCellClick(cell)
                  }
                }

                return (
                  <TableCell
                    key={cell.id}
                    className={cn(
                      'border border-slate-300 text-base',
                      isSticky && `sticky z-18 bg-muted`,
                      isLight && 'bg-gray-300 group-hover:bg-yellow-100',
                      stickyLeft,
                      cell._groupCellClassName,
                      isClickableCell && 'cursor-pointer',
                    )}
                    style={{
                      width:
                        cell.column.getSize() !== 150
                          ? cell.column.getSize()
                          : undefined,
                    }}
                    onClick={handleCellClick}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                )
              })}
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell
              colSpan={table.getAllColumns().length}
              className="h-24 text-center "
            >
              No results.
            </TableCell>
          </TableRow>
        )}
      </TableBody>
      {footer && (
        <TableFooter className="sticky bottom-0 bg-background z-10">
          <TableRow>{footer}</TableRow>
        </TableFooter>
      )}
    </Table>
  )
}

export default DataTableWithBorder
